type SeoOptions = {
  title: string;
  description: string;
  canonicalPath?: string;
  ogImageUrl?: string;
  /** Open Graph type; use "website" for the homepage and "article" for guides. */
  ogType?: string;
  jsonLd?: unknown; // object or array
};

function upsertMetaTag(attrs: { name?: string; property?: string; content: string }) {
  const selector = attrs.name
    ? `meta[name="${attrs.name}"]`
    : `meta[property="${attrs.property ?? ""}"]`;
  const el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (el) {
    el.setAttribute("content", attrs.content);
    return;
  }
  const meta = document.createElement("meta");
  if (attrs.name) meta.setAttribute("name", attrs.name);
  if (attrs.property) meta.setAttribute("property", attrs.property);
  meta.setAttribute("content", attrs.content);
  document.head.appendChild(meta);
}

function upsertCanonical(canonicalUrl: string) {
  let el = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", canonicalUrl);
}

function upsertJsonLd(id: string, jsonLd: unknown) {
  const existing = document.getElementById(id);
  if (existing) existing.remove();

  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = id;
  script.text = JSON.stringify(jsonLd);
  document.head.appendChild(script);
}

export function applySeo(opts: SeoOptions) {
  // Keep this function DOM-safe: it is expected to run in useEffect.
  const titleEl = document.querySelector("title");
  if (titleEl) titleEl.textContent = opts.title;
  document.title = opts.title;

  upsertMetaTag({ name: "description", content: opts.description });

  upsertMetaTag({ property: "og:title", content: opts.title });
  upsertMetaTag({ property: "og:description", content: opts.description });
  upsertMetaTag({ property: "og:type", content: opts.ogType ?? "website" });
  upsertMetaTag({ property: "og:locale", content: "en_US" });
  upsertMetaTag({ property: "og:site_name", content: "Fuwalo" });
  if (opts.ogImageUrl) upsertMetaTag({ property: "og:image", content: opts.ogImageUrl });

  upsertMetaTag({ name: "twitter:card", content: "summary_large_image" });
  upsertMetaTag({ name: "twitter:title", content: opts.title });
  upsertMetaTag({ name: "twitter:description", content: opts.description });
  if (opts.ogImageUrl) upsertMetaTag({ name: "twitter:image", content: opts.ogImageUrl });

  if (opts.canonicalPath) {
    const base = window.location.origin;
    const canonicalUrl = `${base}${opts.canonicalPath.startsWith("/") ? opts.canonicalPath : `/${opts.canonicalPath}`}`;
    upsertCanonical(canonicalUrl);
    upsertMetaTag({ property: "og:url", content: canonicalUrl });
  }

  if (opts.jsonLd) {
    upsertJsonLd("seo-jsonld", opts.jsonLd);
  }
}

export type FaqItem = { q: string; a: string };

export function buildFaqJsonLd(items: FaqItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: it.a,
      },
    })),
  };
}

